"use client";

import * as React from "react";
import { shortenAddress } from "../../../../lib/format";
import { getPortraitUrl } from "../../../wallet-info/constants";
import type { SendFlow } from "../../model/useSendFlow";
import { isAddress } from "../../utils";
import { ClearIcon, PasteIcon } from "../icons";

type Tab = "recent" | "account";

function displayName(
  account: { name?: string; index?: number },
  fallbackIndex: number,
): string {
  return account.name || `Account ${(account.index ?? fallbackIndex) + 1}`;
}

function avatarFallback(label: string): string {
  return label.trim().slice(0, 1).toUpperCase() || "A";
}

function normalizeAddress(address: string): string {
  return address.toLowerCase();
}

export function SendRecipient({ flow }: { flow: SendFlow }) {
  const {
    recipient,
    setRecipient,
    fieldErrors,
    recentAddresses,
    accounts,
    walletAddress,
    resolvedEnv,
    selectRecipient,
  } = flow;

  const myAccounts = React.useMemo(
    () =>
      accounts.filter(
        (a) => a.address.toLowerCase() !== walletAddress.toLowerCase(),
      ),
    [accounts, walletAddress],
  );
  const hasMyAccounts = myAccounts.length > 0;
  const accountByAddress = React.useMemo(() => {
    return new Map(myAccounts.map((account, index) => [
      normalizeAddress(account.address),
      { account, index },
    ]));
  }, [myAccounts]);
  const [tab, setTab] = React.useState<Tab>("recent");
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => { inputRef.current?.focus(); }, []);
  const portraitBg = "1A1A2E";



  const handlePaste = React.useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setRecipient(text.trim());
    } catch {
      /* clipboard denied — user can type manually */
    }
  }, [setRecipient]);

  const filled = recipient.trim().length > 0;
  const valid = isAddress(recipient.trim());

  return (
    <div className="sf-recipient">
      <div className="sf-recipient-top">
      <div
        className="sf-address-field"
        data-filled={filled || undefined}
        data-invalid={fieldErrors.recipient || undefined}
      >
        <textarea
          ref={inputRef}
          className="sf-address-input"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="Enter the recipient's address"
          autoComplete="off"
          spellCheck={false}
          aria-label="Recipient address"
          rows={filled ? 2 : 1}
        />
        {filled ? (
          <button
            type="button"
            className="sf-address-action"
            onClick={() => setRecipient("")}
            aria-label="Clear"
            data-track="recipient_clear"
          >
            <ClearIcon size={20} />
          </button>
        ) : (
          <button
            type="button"
            className="sf-address-action"
            onClick={handlePaste}
            aria-label="Paste from clipboard"
            data-track="recipient_paste"
          >
            <PasteIcon size={20} />
          </button>
        )}
      </div>
      {fieldErrors.recipient && (
        <p className="sf-field-error" role="alert">
          {fieldErrors.recipient}
        </p>
      )}

      <div className="sf-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className="sf-tab"
          data-active={tab === "recent" || undefined}
          aria-selected={tab === "recent"}
          onClick={() => setTab("recent")}
          data-track="recipient_tab"
          data-track-id="recent"
        >
          Recent
        </button>
        {hasMyAccounts && (
          <button
            type="button"
            role="tab"
            className="sf-tab"
            data-active={tab === "account" || undefined}
            aria-selected={tab === "account"}
            onClick={() => setTab("account")}
            data-track="recipient_tab"
            data-track-id="account"
          >
            My Account
          </button>
        )}
      </div>
      </div>

      <div className="sf-address-list">
        {tab === "account" && hasMyAccounts
          ? myAccounts.map((account, index) => {
              const name = displayName(account, index);
              const portrait = getPortraitUrl(resolvedEnv, account.address, {
                size: 56,
                bg: portraitBg,
              });
              return (
                <button
                  key={account.address}
                  type="button"
                  className="sf-address-row"
                  onClick={() => selectRecipient(account.address)}
                  data-track="recipient_item"
                >
                  <span className="sf-address-avatar">
                    {portrait && (
                      <img
                        src={portrait}
                        alt=""
                        width={40}
                        height={40}
                        draggable={false}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    <span className="sf-address-avatar-fallback">
                      {avatarFallback(name)}
                    </span>
                  </span>
                  <span className="sf-address-meta">
                    <span className="sf-address-name">{name}</span>
                    <span className="sf-address-value">
                      {shortenAddress(account.address)}
                    </span>
                  </span>
                </button>
              );
            })
          : recentAddresses.length > 0
            ? recentAddresses.map((item) => {
                const matchedAccount = accountByAddress.get(
                  normalizeAddress(item.address),
                );
                const name = matchedAccount
                  ? displayName(matchedAccount.account, matchedAccount.index)
                  : shortenAddress(item.address);
                const portrait = getPortraitUrl(resolvedEnv, item.address, {
                  size: 56,
                  bg: portraitBg,
                });
                return (
                  <button
                    key={item.address}
                    type="button"
                    className="sf-address-row"
                    onClick={() => selectRecipient(item.address)}
                    data-track="recipient_item"
                  >
                    <span className="sf-address-avatar">
                      {portrait && (
                        <img
                          src={portrait}
                          alt=""
                          width={40}
                          height={40}
                          draggable={false}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      <span className="sf-address-avatar-fallback">
                        {avatarFallback(name)}
                      </span>
                    </span>
                    <span className="sf-address-meta">
                      <span className="sf-address-name">{name}</span>
                      <span className="sf-address-value">
                        {shortenAddress(item.address)}
                      </span>
                    </span>
                  </button>
                );
              })
            : (
              <div className="sf-empty">No send history</div>
            )}
      </div>

      {filled && !valid && !fieldErrors.recipient && (
        <p className="sf-field-error" role="alert">
          Enter a valid EVM recipient address.
        </p>
      )}
    </div>
  );

}
